import {
  PlayStyleUpgradeIcon,
  UpgradeChip,
} from "@/design-system/Evolution/UpgradeChip";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { playerRoleLabelSelector } from "@/redux/players/playersSlice";
import {
  type GroupedUpgrades,
  groupUpgrades,
  rolePlusMark,
  rolePlusTier,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import type { RolesPlusUpgrade, UpgradeType } from "@repo/shared/types";
import { inGameAttributesDict } from "@repo/shared/utils";
import { ReactNode, useMemo } from "react";
import { useSelector } from "react-redux";

export { PlayStyleUpgradeIcon, UpgradeChip };

export const RolesPlusUpgradeChip = ({
  upgrade,
  className,
}: {
  upgrade: RolesPlusUpgrade;
  className?: string;
}) => {
  const { roleId, plusPlus } = rolePlusTier(upgrade.roleId);
  const playerRoleName = useSelector(
    playerRoleLabelSelector({ playerRoleId: roleId, shortName: true }),
  );
  return (
    <UpgradeChip
      label={playerRoleName}
      value={rolePlusMark(plusPlus)}
      showArrow={false}
      className={className}
    />
  );
};

/** A step's upgrades split by kind, so a card can lay them out in rows. */
/**
 * The chips a step's upgrades turn into, in three rows: the numbers (OVR,
 * face stats, in-game stats, weak foot, skill moves), what is new (positions,
 * role pluses) and the PlayStyles. In-game stats only show when no face stat
 * moved, as the face stat already sums them up.
 */
export const useUpgradeChipRows = (
  upgrades: UpgradeType[],
): { grouped: GroupedUpgrades; rows: ReactNode[][] } => {
  const { t } = useTranslation("evoBuilder");
  const { playerAttributes } = useSelector(contentLabelsSelector);
  const grouped = useMemo(() => groupUpgrades(upgrades), [upgrades]);

  const rows = useMemo(() => {
    const showInGameAttrs =
      grouped.attributes.reduce((acc, u) => acc + u.value, 0) === 0;
    const numbers: ReactNode[] = [
      ...(grouped.ovr
        ? [
            <UpgradeChip
              key="ovr"
              label={t("chips.ovr")}
              value={grouped.ovr.value}
              capped={grouped.ovr.capped}
            />,
          ]
        : []),
      ...grouped.attributes.map((upgrade, i) => (
        <UpgradeChip
          key={`attr-${i}`}
          label={playerAttributes[upgrade.attributeKey]?.short ?? upgrade.label}
          value={upgrade.value}
          capped={upgrade.capped}
        />
      )),
      ...(showInGameAttrs
        ? grouped.inGameAttributes.map((upgrade, i) => (
            <UpgradeChip
              key={`iga-${i}`}
              label={
                playerAttributes[upgrade.attributeKey]?.label ??
                inGameAttributesDict[upgrade.attributeKey]?.label ??
                ""
              }
              value={upgrade.value}
              capped={upgrade.capped}
            />
          ))
        : []),
      ...grouped.weakFoot.map((upgrade, i) => (
        <UpgradeChip
          key={`wf-${i}`}
          label={t("chips.wf")}
          value={upgrade.weakFoot}
          capped={upgrade.capped}
        />
      )),
      ...grouped.skillMoves.map((upgrade, i) => (
        <UpgradeChip
          key={`sm-${i}`}
          label={t("chips.sm")}
          value={upgrade.skillMoves}
          capped={upgrade.capped}
        />
      )),
    ];
    const grants: ReactNode[] = [
      ...grouped.positions.map((upgrade, i) => (
        <UpgradeChip key={`pos-${i}`} label={upgrade.positionId} accent />
      )),
      ...grouped.rolesPlus.map((upgrade, i) => (
        <RolesPlusUpgradeChip key={`role-${i}`} upgrade={upgrade} />
      )),
      ...(grouped.accelerate
        ? [
            <UpgradeChip
              key="accelerate"
              label={
                <>
                  {t(`players:filters.accelerates.${grouped.accelerate.from}`)}{" "}
                  {/* Red when the step lowers the meta rating; a rise may come from other stats, so grey */}
                  <span
                    className={
                      (grouped.metaRating?.metaRating.delta ?? 0) < 0
                        ? "text-error"
                        : "text-foreground-muted"
                    }
                  >
                    →
                  </span>{" "}
                  {t(`players:filters.accelerates.${grouped.accelerate.to}`)}
                </>
              }
            />,
          ]
        : []),
    ];
    const playStyles: ReactNode[] = [
      ...grouped.playStylesPlus.map((upgrade, i) => (
        <PlayStyleUpgradeIcon
          key={`psp-${i}`}
          playStyleId={upgrade.playStyleId}
          isPlus
        />
      )),
      ...grouped.playStyles.map((upgrade, i) => (
        <PlayStyleUpgradeIcon
          key={`ps-${i}`}
          playStyleId={upgrade.playStyleId}
          isPlus={false}
        />
      )),
    ];
    return [numbers, grants, playStyles].filter((row) => row.length > 0);
  }, [grouped, playerAttributes, t]);

  return { grouped, rows };
};
